import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { Paper, TextField, Button, Autocomplete } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import SkeletonLoader from './SkeletonLoader';
import { fetchMetaInsigthByEventIDs, fetchTicketureByEventIDs, fetchTicketureByOpenDate, fetchTicketureAllEventIDs } from '../services/ticketure';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../store/drilldownSlice';
import { excludeCancelledEvents } from '../utils/excludeCancelledEvents';
import dayjs from 'dayjs';
import DrillDownChart from './UI-components/drilldown/chart';



  const usdPrice = {
    type: 'number',
    width: 130,
    valueFormatter: (params) => {
      const value = params || 0;
      const wholeNumValue = Number(value).toFixed(0) 
      return `$${Number(wholeNumValue).toLocaleString()}`;
    },
  };

  const largeNumber = {
    type: 'number',
    width: 130,
    valueFormatter: (params) => {
      const value = params || 0;
      const wholeNumValue = Number(value).toFixed(0);
      return Number(wholeNumValue).toLocaleString();
    },
  };

  const decimalNumber = {
    type: 'number',
    width: 90
  };

const Drilldown = () => {

  const handlePasteEventIDs = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
  
    // Acceptable delimiters: comma, newline, tab, semicolon
    const rawIDs = pastedText
      .split(/[,;\n\t]+/)
      .map(id => id.trim())
      .filter(Boolean);
  
    // Validate against available event IDs
    const validEventIDs = rawIDs.filter(id => state?.allEventIds?.includes(id));
    const invalidEventIDs = rawIDs.filter(id => !state?.allEventIds?.includes(id));
  
    // Combine with existing selections, removing duplicates
    const combinedIDs = Array.from(new Set([...selectedEventIDs, ...validEventIDs]));
    setSelectedEventIDs(combinedIDs);
  
    if (invalidEventIDs.length > 0) {
      alert(`Some Event IDs were not recognized and were not added: \n${invalidEventIDs.join(', ')}`);
    }
  };
  
  const columns = [
    { field: 'week_number', headerName: "Week Number", type: 'number', headerAlign: 'center', align: 'center' },
    { field: 'day_of_week', headerName: "Day of Week", type: 'string', headerAlign: 'center', align: 'center' },
    { field: 'sale_date', headerName: "Sale Date", type: 'string', headerAlign: 'center', align: 'center' },
    { field: 'event_id', headerName: "Event ID", type: 'string', headerAlign: 'center', align: 'center' },
    { field: 'market_name', headerName: "Market Name", type: 'string', headerAlign: 'center', align: 'center' },
    { field: 'generalized_open_date', headerName: "Show Date", type: 'string', headerAlign: 'center', align: 'center' },
    //{ field: 'objective', headerName: "Objective", type: 'string', headerAlign: 'center', align: 'center' },
    { field: 'median_income', headerName: "Median Income", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'digital_audience_size', headerName: "Digital Audience Size", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'days_out', headerName: "Days Out", type: 'number', headerAlign: 'center', align: 'center' },
    { field: 'marketing_phase', headerName: "Marketing Phase", type: 'string', headerAlign: 'center', align: 'center' },
    { field: 'daily_ticket_count', headerName: "Daily Ticket Count", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'daily_merch_count', headerName: "Daily Add-On Count", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'daily_orders', headerName: "Daily Orders", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'total_orders', headerName: "Total Orders", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'daily_ticket_revenue', headerName: "Daily Ticket Revenue", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'daily_merch_revenue', headerName: "Daily Add-On Revenue", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'daily_ticket_fees', headerName: "Daily Ticket Fees", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'daily_ticket_taxes', headerName: "Daily Ticket Taxes", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'cumulative_tickets', headerName: "Total Tickets", ...largeNumber, headerAlign: 'center', align: 'center' },
    //{ field: 'cumulative_merch', headerName: "Total Add-Ons", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'cumulative_ticket_revenue', headerName: "Total Revenue", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'running_avg_daily_tickets', headerName: "Avg Tickets", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'spend', headerName: "Spend", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'cumulative_spend', headerName: "Total Spend", ...usdPrice, headerAlign: 'center', align: 'center' },
    { field: 'roas', headerName: "ROAS", type: 'number', headerAlign: 'center', align: 'center', valueFormatter: (params) => `${(params || 0).toFixed(0).toLocaleString()} %` },
    { field: 'trailing_7_day_roas', headerName: "7-Day ROAS", type: 'number', headerAlign: 'center', align: 'center', valueFormatter: (params) => `${(params || 0).toFixed(0).toLocaleString()} %` },
    { field: 'impressions', headerName: "Impressions", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'inline_link_clicks', headerName: "Link Clicks", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'clicks', headerName: "Clicks", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'unique_clicks', headerName: "Unique Clicks", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'engagements', headerName: "Engagements", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'comments', headerName: "Comments", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'post_reactions', headerName: "Post Reactions", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'purchases', headerName: "Purchases", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'reach', headerName: "Reach", ...largeNumber, headerAlign: 'center', align: 'center' },
    { field: 'frequency', headerName: "Frequency", type: 'number', headerAlign: 'center', align: 'center', ...decimalNumber, valueFormatter: (params) => (params || 0).toFixed(2) },
    { field: 'daily_cpt', headerName: "Daily CPT", type: 'number', headerAlign: 'center', align: 'center', ...decimalNumber },
    { field: 'cpt', headerName: 'CPT', type: 'number', headerAlign: 'center', align: 'center', ...decimalNumber },
    { field: 'ctr', headerName: "CTR", type: 'number', headerAlign: 'center', align: 'center', ...decimalNumber },
    { field: 'cpm', headerName: "CPM", type: 'number', headerAlign: 'center', align: 'center', ...decimalNumber },
    { field: 'cpe', headerName: "CPE", type: 'number', headerAlign: 'center', align: 'center', ...decimalNumber },
  ];

  // Inside Drilldown component
  const allMetrics = [
    'daily_ticket_count',
    'daily_merch_count',
    'daily_ticket_revenue',
    'daily_merch_revenue',
    'daily_orders',
    'daily_ticket_fees',
    'daily_ticket_taxes',
    'cumulative_tickets',
    'total_orders',
    'cumulative_ticket_revenue',
    'spend',
    'cumulative_spend',
    'roas',
    'trailing_7_day_roas',
    'impressions',
    'clicks',
    'unique_clicks',
    'engagements',
    'comments',
    'post_reactions',
    'purchases',
    'reach',
    'frequency',
    'ctr',
    'cpm',
    'cpe',
    'daily_cpt',
    'cpt',
    'running_avg_daily_tickets',
  ];  

  const [selectedEventIDs, setSelectedEventIDs] = useState([]);
  const [appliedEventIDs, setAppliedEventIDs] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [error, setError] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    week_number: false,
    day_of_week: false,
    median_income: false,
    digital_audience_size: false,
  });

  const state = useSelector((state) => state.ticketure);
  const companyName = useSelector((state) => state.company.company);

  const { data: staticData } = useQuery("forecastData", async () => {
    const response = await axios.get("https://fqe-analytics-server-e1c19d4ce6cf.herokuapp.com/api/forecast-data");
    return response.data;
  });

  const dispatch = useDispatch();
  const loading = useSelector((state) => state.drilldown.loading);

  // Date calculations
  const today = dayjs();
  const startDate = today.format('YYYY-MM-DD');
  const endDate = today.add(2, 'week').format('YYYY-MM-DD');

  // Helper function to normalize strings
  const normalizeString = (str) => str.trim().toLowerCase();

  function aggregateMetaData(data) {
    const grouped = {};
  
    data.forEach(item => {
      // Create a composite key for grouping (event_id + sale_date)
      const key = `${item.event_id}-${item.date_start}`;
  
      if (!grouped[key]) {
        // Clone the item so we can safely modify it
        grouped[key] = { ...item };
      } else {
        // Sum additive metrics
        grouped[key].spend += Number(item.spend) || 0;
        grouped[key].impressions += Number(item.impressions) || 0;
        grouped[key].clicks += Number(item.clicks) || 0;
        grouped[key].unique_clicks += Number(item.unique_clicks) || 0;
        grouped[key].inline_link_clicks += Number(item.inline_link_clicks) || 0;
        grouped[key].engagements += Number(item.engagements) || 0;
        grouped[key].comments += Number(item.comments) || 0;
        grouped[key].post_reactions += Number(item.post_reactions) || 0;
        grouped[key].purchases += Number(item.purchases) || 0;
        grouped[key].reach += Number(item.reach) || 0;
      }
    });
  
    // Return the aggregated array
    return Object.values(grouped);
  }

  // Fetch event data based on companyName
  useEffect(() => {
    dispatch(fetchTicketureAllEventIDs());
    dispatch(fetchTicketureByOpenDate({ startDate, endDate }));
  }, [dispatch, startDate, endDate, companyName]);

  // Set applied event IDs
  useEffect(() => {
    if (state?.defaultEventIDs && state?.defaultEventIDs.length > 0) {
      const uniqueEventIDs = Array.from(new Set(state?.defaultEventIDs.map(event => event.event_id)));
      const validEventIDs = excludeCancelledEvents(uniqueEventIDs);
      setAppliedEventIDs(validEventIDs);
    }
  }, [state?.defaultEventIDs]);

  useEffect(() => {
    if (appliedEventIDs.length > 0) {
      const fetchAllEventData = async () => {
        setError(null);
        try {
          dispatch(setLoading({ isLoading: true, type: "dailyTicketSalesMetaInsight" }));
          const allEventData1 = await Promise.all(
            appliedEventIDs.map((eventID) => dispatch(fetchTicketureByEventIDs(eventID)))
          );
          const allEventData2 = await Promise.all(
            appliedEventIDs.map((eventID) => dispatch(fetchMetaInsigthByEventIDs(eventID)))
          );
          const rawData1 = allEventData1.flatMap((response) => response.payload);
          const rawData2 = allEventData2.flatMap((response) => response.payload);

          const mergedMarketingData = aggregateMetaData(rawData2);

          // Build map of event show dates for filling missing values
          const eventShowDateMap = {};
          rawData1.forEach(item => {
            if (item.generalized_open_date && !eventShowDateMap[item.event_id]) {
              eventShowDateMap[item.event_id] = item.generalized_open_date;
            }
          });

          // Perform full outer join on ticket and marketing data by event_id and date
          const ticketMap = {};
          rawData1.forEach((item) => {
            const key = `${item.event_id}-${item.sale_date}`;
            ticketMap[key] = item;
          });
          const marketingMap = {};
          mergedMarketingData.forEach((item) => {
            const key = `${item.event_id}-${item.date_start}`;
            marketingMap[key] = item;
          });
          const allKeys = new Set([
            ...Object.keys(ticketMap),
            ...Object.keys(marketingMap),
          ]);
          const filterArr = Array.from(allKeys).map((key) => {
            const sale_date = key.slice(-10);
            const event_id = key.slice(0, key.length - 11);
            const ticketItem = ticketMap[key];
            const marketingItem = marketingMap[key];
            const combinedItem = {
              id: key,
              event_id,
              sale_date,
              market_name: ticketItem?.market_name ?? marketingItem?.market_name ?? null,
              generalized_open_date: ticketItem?.generalized_open_date ?? null,
              days_out: ticketItem?.days_out ?? null,
              daily_ticket_count: Number(ticketItem?.daily_ticket_count) || 0,
              daily_merch_count: Number(ticketItem?.daily_merch_count) || 0,
              daily_orders: Number(ticketItem?.daily_orders) || 0,
              daily_ticket_revenue: Number(ticketItem?.daily_ticket_revenue) || 0,
              daily_merch_revenue: Number(ticketItem?.daily_merch_revenue) || 0,
              daily_ticket_fees: Number(ticketItem?.daily_ticket_fees) || 0,
              daily_ticket_taxes: Number(ticketItem?.daily_ticket_taxes) || 0,
              cumulative_tickets: Number(ticketItem?.cumulative_tickets) || 0,
              cumulative_merch: Number(ticketItem?.cumulative_merch) || 0,
              total_orders: Number(ticketItem?.total_orders) || 0,
              cumulative_ticket_revenue: Number(ticketItem?.cumulative_ticket_revenue) || 0,
              cumulative_merch_revenue: Number(ticketItem?.cumulative_merch_revenue) || 0,
              spend: Number(marketingItem?.spend) || 0,
              cumulative_spend: Number(marketingItem?.cumulative_spend) || 0,
              impressions: Number(marketingItem?.impressions) || 0,
              clicks: Number(marketingItem?.clicks) || 0,
              unique_clicks: Number(marketingItem?.unique_clicks) || 0,
              inline_link_clicks: Number(marketingItem?.inline_link_clicks) || 0,
              engagements: Number(marketingItem?.engagements) || 0,
              frequency: Number(marketingItem?.frequency) || 0,
              comments: Number(marketingItem?.comments) || 0,
              post_reactions: Number(marketingItem?.post_reactions) || 0,
              purchases: Number(marketingItem?.purchases) || 0,
              reach: Number(marketingItem?.reach) || 0,
            };
            // Initial week/day based on sale_date
            const saleDateObj = dayjs(sale_date);
            combinedItem.week_number = saleDateObj.isValid() ? saleDateObj.week() : null;
            combinedItem.day_of_week = saleDateObj.isValid() ? saleDateObj.format('dddd') : null;

            // Ensure show date is populated
            const showDate = combinedItem.generalized_open_date || eventShowDateMap[event_id] || null;
            combinedItem.generalized_open_date = showDate;

            // Compute days_out if missing
            if (combinedItem.days_out === null && showDate) {
              combinedItem.days_out = dayjs(showDate).diff(dayjs(combinedItem.sale_date), 'day');
            }

            // Calculate marketing_phase
            let phase = null;
            const dOut = combinedItem.days_out;
            if (dOut !== null) {
              if (dOut >= 121) phase = 1;
              else if (dOut >= 31) phase = 2;
              else if (dOut >= 15) phase = 3;
              else if (dOut >= 8) phase = 4;
              else if (dOut >= -5) phase = 5;
            }
            combinedItem.marketing_phase = phase;

            // Compute daily CPT
            {
              const dtCount = combinedItem.daily_ticket_count;
              let dailyCpt = 0;
              if (dtCount > 0) {
                dailyCpt = combinedItem.spend / dtCount;
              } else if (dtCount === 0 && combinedItem.spend > 0) {
                dailyCpt = Infinity;
              }
              combinedItem.daily_cpt = parseFloat(dailyCpt.toFixed(2));
            }
            // Compute CTR, CPM, CPE
            combinedItem.ctr = combinedItem.impressions > 0 ? (combinedItem.clicks / combinedItem.impressions) * 100 : 0;
            combinedItem.cpm = combinedItem.impressions > 0 ? (combinedItem.spend / combinedItem.impressions) * 1000 : 0;
            combinedItem.cpe = combinedItem.engagements > 0 ? combinedItem.spend / combinedItem.engagements : 0;

            return combinedItem;
          });

          // Sort the data by event_id and sale_date ascending
          filterArr.sort((a, b) => {
            if (a.event_id === b.event_id) {
              return dayjs(a.sale_date).valueOf() - dayjs(b.sale_date).valueOf();
            } else {
              return a.event_id.localeCompare(b.event_id);
            }
          });

          // Initialize cumulative variables per event_id
          const cumulativeRevenuePerEvent = {};
          const cumulativeSpendPerEvent = {};
          const cumulativeTicketsPerEvent = {};
          const cumulativeMerchPerEvent = {};
          const cumulativeMerchRevenuePerEvent = {};
          const cumulativeOrdersPerEvent = {};
          const firstSaleDatePerEvent = {};
          const staticDataMap = {};
          if (Array.isArray(staticData)) {
            staticData.forEach((item) => {
              const clusterMarket = item["Cluster Market"];
              if (!clusterMarket) {
                return;
              }
              const normalizedClusterMarket = normalizeString(clusterMarket);
              staticDataMap[normalizedClusterMarket] = item;
            });
          }

          // Iterate over the sorted data to calculate cumulative values per event_id
          const dataWithCalculations = filterArr.map((item, idx, arr) => {
            const eventId = item.event_id;

            // Initialize cumulative revenue and spend for the event_id if not already done
            if (!cumulativeRevenuePerEvent[eventId]) {
              cumulativeRevenuePerEvent[eventId] = 0;
            }
            if (!cumulativeSpendPerEvent[eventId]) {
              cumulativeSpendPerEvent[eventId] = 0;
            }

            // Set the first sale date per event_id
            if (!firstSaleDatePerEvent[eventId]) {
              firstSaleDatePerEvent[eventId] = dayjs(item.sale_date);
            }

            // Accumulate tickets, revenue, and spend
            cumulativeTicketsPerEvent[eventId] = (cumulativeTicketsPerEvent[eventId] || 0) + (Number(item.daily_ticket_count) || 0);
            cumulativeRevenuePerEvent[eventId] += Number(item.daily_ticket_revenue) || 0;
            cumulativeSpendPerEvent[eventId] += Number(item.spend) || 0;
            cumulativeMerchPerEvent[eventId] = (cumulativeMerchPerEvent[eventId] || 0) + (Number(item.daily_merch_count) || 0);
            cumulativeMerchRevenuePerEvent[eventId] = (cumulativeMerchRevenuePerEvent[eventId] || 0) + (Number(item.daily_merch_revenue) || 0);
            cumulativeOrdersPerEvent[eventId] = (cumulativeOrdersPerEvent[eventId] || 0) + (Number(item.daily_orders) || 0);

            // Calculate days since first sale (inclusive)
            const daysSinceFirstSale = dayjs(item.sale_date).diff(firstSaleDatePerEvent[eventId], 'day') + 1;

            // Calculate running average using cumulative tickets
            const runningAverageTicketsSold = parseFloat(
              (cumulativeTicketsPerEvent[eventId] / daysSinceFirstSale).toFixed(2)
            );

            // Calculate Cost Per Ticket (CPT) using cumulative values
            let cpt = null;
            const cumulativeTicketsSold = cumulativeTicketsPerEvent[eventId] || 0;
            const cumulativeSpend = cumulativeSpendPerEvent[eventId];
            if (cumulativeTicketsSold > 0) {
              cpt = cumulativeSpend / cumulativeTicketsSold;
            } else if (cumulativeTicketsSold === 0 && cumulativeSpend > 0) {
              cpt = Infinity;
            } else {
              cpt = 0;
            }
            // Format CPT to two decimals
            cpt = parseFloat(cpt.toFixed(2));

            // Calculate ROAS
            const roas = cumulativeRevenuePerEvent[eventId] / cumulativeSpendPerEvent[eventId]

            // Calculate trailing 7-day ROAS
            const history = arr
              .slice(0, idx + 1)
              .filter(r => r.event_id === eventId);
            const window7 = history.slice(-7);
            const revenue7d = window7.reduce(
              (sum, r) => sum + (Number(r.daily_ticket_revenue) || 0),
              0
            );
            const spend7d = window7.reduce(
              (sum, r) => sum + (Number(r.spend) || 0),
              0
            );
            const trailing_7_day_roas = spend7d > 0
              ? (revenue7d / spend7d) * 100
              : 0;

            // Map static data based on market_name matching "Cluster Market"
            const marketName = item.market_name;
            const normalizedMarketName = normalizeString(marketName);
            const staticItem = staticDataMap[normalizedMarketName];

            // Initialize variables to hold the static data values
            let medianIncome = null;
            let digitalAudienceSize = null;

            if (staticItem) {
              medianIncome = staticItem["Median Income ($)"];
              digitalAudienceSize = staticItem["Digital Audience Size (20 Mile)"];
            } else {
              console.warn(`No static data found for market: ${marketName}`);
            }

            return {
              ...item,
              // Override raw cumulative fields with computed rolling totals
              cumulative_tickets: cumulativeTicketsPerEvent[eventId],
              cumulative_ticket_revenue: cumulativeRevenuePerEvent[eventId],
              cumulative_spend: Math.round(cumulativeSpendPerEvent[eventId]),
              cumulative_merch: cumulativeMerchPerEvent[eventId],
              cumulative_merch_revenue: cumulativeMerchRevenuePerEvent[eventId],
              total_orders: cumulativeOrdersPerEvent[eventId],
              running_avg_daily_tickets: runningAverageTicketsSold,
              roas: roas * 100,
              trailing_7_day_roas: trailing_7_day_roas,
              cpt,
              median_income: medianIncome,
              digital_audience_size: digitalAudienceSize,
            };
          });

          setEventData(dataWithCalculations);
          dispatch(setLoading({ isLoading: false, type: "" }))
        } catch (error) {
          setError(error);
          dispatch(setLoading({ isLoading: false, type: "" }));
        }
      };
      fetchAllEventData();
    }
  }, [dispatch, appliedEventIDs, staticData]);

  const handleApply = () => {
    setAppliedEventIDs([...selectedEventIDs]);
  };

  const handleExportCSV = () => {
    const csvRows = [];
    const headers = [
      'week_number',
      'day_of_week',
      'sale_date',
      'event_id',
      'market_name',
      'generalized_open_date',
      'median_income',
      'digital_audience_size',
      'days_out',
      'marketing_phase',
      'daily_ticket_count',
      'daily_orders',
      'daily_ticket_revenue',
      'daily_merch_revenue',
      'total_revenue',
      'total_merch_revenue',
      'cumulative_spend',
      'roas',
      'running_avg_daily_tickets',
      'cost_per_ticket',
      'cumulative_tickets',
      'spend',
      'impressions',
      'inline_link_clicks',
      'clicks',
      'unique_clicks',
      'engagements',
      'comments',
      'post_reactions',
      'purchases',
      'reach',
      'frequency',
      'daily_cpt',
      'cpt',
      'ctr',
      'cpm',
      'cpe',
    ];
    csvRows.push(headers.join(','));

    eventData.forEach((row) => {
      const values = [
        row.week_number,
        row.day_of_week,
        row.sale_date,
        row.event_id,
        row.market_name,
        row.generalized_open_date,
        row.median_income,
        row.digital_audience_size,
        row.days_out,
        row.marketing_phase,
        row.daily_ticket_count,
        row.daily_ticket_revenue,
        row.total_revenue,
        row.cumulative_spend,
        row.roas,
        row.running_avg_daily_tickets,
        row.cpt,
        row.cumulative_tickets,
        row.spend,
        row.impressions,
        row.inline_link_clicks,
        row.clicks,
        row.unique_clicks,
        row.engagements,
        row.comments,
        row.post_reactions,
        row.purchases,
        row.reach,
        row.frequency,
        row.cpt,
        row.ctr,
        row.cpm,
        row.cpe,
      ];

      // Map over values to handle commas and quotes
      const formattedValues = values.map((value) => {
        if (value === null || value === undefined) {
          return '';
        }
        const stringValue = value.toString();
        if (/[",\n]/.test(stringValue)) {
          const escapedValue = stringValue.replace(/"/g, '""');
          return `"${escapedValue}"`;
        }
        return stringValue;
      });
      csvRows.push(formattedValues.join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'drilldown.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const defaultMetricsChart3 = ['daily_ticket_count'];
  const defaultMetricsChart2 = ['spend'];

  return (
    <Paper style={{ padding: '10px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Autocomplete
            multiple
            options={state?.allEventIds || []}
            getOptionLabel={(option) => option}
            value={selectedEventIDs}
            onChange={(event, newValue) => setSelectedEventIDs(newValue)}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Event IDs"
                onPaste={handlePasteEventIDs}
              />
            )}
            style={{ width: '1000px', marginRight: '20px' }}
          />
          <Button
            variant="contained"
            color="primary"
            style={{ width: '100px', fontWeight: '900', marginRight: '20px', maxHeight: '50px' }}
            onClick={handleApply}
          >
            Apply
          </Button>
          <Button
            variant="contained"
            onClick={handleExportCSV}
            style={{ fontWeight: '900', marginRight: '20px', maxHeight: '50px' }}
          >
            Export CSV
          </Button>
        </div>
      </div>
      {error && (
        <div style={{ color: 'red', textAlign: 'center', marginBottom: '10px' }}>
          Failed to load drilldown data. {error.message || 'Unknown error'}
        </div>
      )}
      {loading.isLoading && loading.type === 'dailyTicketSalesMetaInsight' ? (
        <SkeletonLoader loading={loading.isLoading} width="100%" height={450} />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div
            style={{
              flex: 1,
              margin: '10px',
              backgroundColor: '#fdfdfd',
              border: '1px solid #f1f2f6',
              borderRadius: '5px',
            }}
          >
            <DrillDownChart
              eventData={eventData}
              allMetrics={allMetrics}
              defaultSelectedMetrics={defaultMetricsChart3}
            />
          </div>
          <div
            style={{
              flex: 1,
              margin: '10px',
              backgroundColor: '#fdfdfd',
              border: '1px solid #f1f2f6',
              borderRadius: '5px',
            }}
          >
            <DrillDownChart
              eventData={eventData}
              allMetrics={allMetrics}
              defaultSelectedMetrics={defaultMetricsChart2}
            />
          </div>
        </div>

      )}
      {loading.isLoading && loading.type === 'dailyTicketSalesMetaInsight' ? (
        <SkeletonLoader loading={loading.isLoading} width="100%" height={450} />
      ) : (
        <div style={{ height: "70vh", width: '100%', overflowX: "auto", position: "relative", margin: 'auto' }}>
          <DataGrid
            columns={columns}
            rows={eventData}
            pageSize={5}
            slots={{ toolbar: GridToolbar }}
            style={{ width: "100%", minWidth: '600px', position: "absolute" }}
            showCellVerticalBorder
            showColumnVerticalBorder
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
          />
        </div>
      )}
    </Paper>
  );
};

export default Drilldown;
